import React, { useState, useEffect } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { toast } from 'react-toastify';
import moment from 'moment';
import HeaderAzul from '~/Components/HeaderAzul/HeaderAzul';
import Loading from '~/Components/Loading';
import * as api from '~/Services/Service';
import FiltrosRelatorio from './FiltrosRelatorio';
import TabelaResultado from './TabelaResultado';

const filtrosIniciais = {
  codigo_grupo_empresa: null,
  codigo_grupo_ge: null,
  empreendimentos_selecionados: [],
  data_inicial: moment()
    .startOf('month')
    .format('DD/MM/YYYY'),
  data_final: moment().format('DD/MM/YYYY'),
  codigo_ge_vendedor: null,
  codigo_gerente: null,
  codigo_supervisor: null,
  codigo_assessor: null,
  codigo_corretor: null,
  periodo_referente: 'E',
  tipos_empreendimento: ['B', 'L', 'V', 'H'],
  filtro_gerencial: 'A',
  tipo_vv: 'S',
  excluir_transferencias: 'N',
  listar_historico: 'N',
  ocultar_sinal_parceiro: 'N',
  somente_int_vendas: 'N',
  considerar_responsavel: 'N',
  filtrar_rescisao_data: 'N',
};

const RelatorioVendasValorVista = () => {
  const [dados, setDados] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [grupoEmpresas, setGrupoEmpresas] = useState([]);
  const [gruposGe, setGruposGe] = useState([]);
  const [empreendimentos, setEmpreendimentos] = useState([]);
  const [hierarquia, setHierarquia] = useState({
    gerentes: [],
    supervisores: [],
    assessores: [],
    corretores: [],
  });
  const [filtros, setFiltros] = useState(filtrosIniciais);
  const [filtrosSubmetidos, setFiltrosSubmetidos] = useState(null);
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    const carregarOpcoes = async () => {
      try {
        const [ges, grGes] = await Promise.all([
          api.listarGrupoEmpresaVendasVista(),
          api.listarGrupoGeVendasVista(),
        ]);
        setGrupoEmpresas(ges || []);
        setGruposGe(grGes || []);
      } catch (err) {
        const msg =
          err && err.message ? err.message : 'Erro ao carregar opções';
        toast.error(msg);
      }
    };
    carregarOpcoes();
  }, []);

  useEffect(() => {
    if (!filtros.codigo_grupo_empresa) return;
    const carregar = async () => {
      try {
        const emps = await api.listarEmpreendimentosVendasVista({
          codigo_grupo_empresa: filtros.codigo_grupo_empresa,
          codigo_grupo_ge: null,
        });
        setEmpreendimentos(emps || []);
      } catch (err) {
        const msg =
          err && err.message ? err.message : 'Erro ao carregar empreendimentos';
        toast.error(msg);
      }
    };
    carregar();
  }, [filtros.codigo_grupo_empresa]);

  useEffect(() => {
    if (!filtros.codigo_grupo_ge) return;
    const carregar = async () => {
      try {
        const emps = await api.listarEmpreendimentosVendasVista({
          codigo_grupo_empresa: null,
          codigo_grupo_ge: filtros.codigo_grupo_ge,
        });
        setEmpreendimentos(emps || []);
      } catch (err) {
        const msg =
          err && err.message ? err.message : 'Erro ao carregar empreendimentos';
        toast.error(msg);
      }
    };
    carregar();
  }, [filtros.codigo_grupo_ge]);

  useEffect(() => {
    if (!filtros.codigo_ge_vendedor) {
      setHierarquia({
        gerentes: [],
        supervisores: [],
        assessores: [],
        corretores: [],
      });
      return;
    }
    const carregar = async () => {
      try {
        const gerentes = await api.listarHierarquiaComercialVendasVista({
          codigo_grupo_empresa: filtros.codigo_ge_vendedor,
          tipo_comissao: 'GER',
          lista_cod_superiores: null,
        });
        setHierarquia(prev => ({
          ...prev,
          gerentes: gerentes || [],
          supervisores: [],
          assessores: [],
          corretores: [],
        }));
      } catch (err) {
        const msg =
          err && err.message ? err.message : 'Erro ao carregar gerentes';
        toast.error(msg);
      }
    };
    carregar();
  }, [filtros.codigo_ge_vendedor]);

  useEffect(() => {
    if (!filtros.codigo_gerente) {
      setHierarquia(prev => ({
        ...prev,
        supervisores: [],
        assessores: [],
        corretores: [],
      }));
      return;
    }
    const carregar = async () => {
      try {
        const supervisores = await api.listarHierarquiaComercialVendasVista({
          codigo_grupo_empresa: filtros.codigo_ge_vendedor,
          tipo_comissao: 'SUP',
          lista_cod_superiores: String(filtros.codigo_gerente),
        });
        setHierarquia(prev => ({
          ...prev,
          supervisores: supervisores || [],
          assessores: [],
          corretores: [],
        }));
      } catch (err) {
        const msg =
          err && err.message ? err.message : 'Erro ao carregar supervisores';
        toast.error(msg);
      }
    };
    carregar();
  }, [filtros.codigo_gerente]);

  useEffect(() => {
    if (!filtros.codigo_supervisor) {
      setHierarquia(prev => ({ ...prev, assessores: [], corretores: [] }));
      return;
    }
    const carregar = async () => {
      try {
        const assessores = await api.listarHierarquiaComercialVendasVista({
          codigo_grupo_empresa: filtros.codigo_ge_vendedor,
          tipo_comissao: 'EQP',
          lista_cod_superiores: String(filtros.codigo_supervisor),
        });
        setHierarquia(prev => ({
          ...prev,
          assessores: assessores || [],
          corretores: [],
        }));
      } catch (err) {
        const msg =
          err && err.message ? err.message : 'Erro ao carregar assessores';
        toast.error(msg);
      }
    };
    carregar();
  }, [filtros.codigo_supervisor]);

  useEffect(() => {
    if (!filtros.codigo_assessor) {
      setHierarquia(prev => ({ ...prev, corretores: [] }));
      return;
    }
    const carregar = async () => {
      try {
        const corretores = await api.listarHierarquiaComercialVendasVista({
          codigo_grupo_empresa: filtros.codigo_ge_vendedor,
          tipo_comissao: 'COR',
          lista_cod_superiores: String(filtros.codigo_assessor),
        });
        setHierarquia(prev => ({ ...prev, corretores: corretores || [] }));
      } catch (err) {
        const msg =
          err && err.message ? err.message : 'Erro ao carregar corretores';
        toast.error(msg);
      }
    };
    carregar();
  }, [filtros.codigo_assessor]);

  const handleFiltroChange = (campo, valor) => {
    setFiltros(prev => {
      const novo = { ...prev, [campo]: valor };
      if (campo === 'codigo_grupo_empresa' && valor) {
        novo.codigo_grupo_ge = null;
        novo.empreendimentos_selecionados = [];
      }
      if (campo === 'codigo_grupo_ge' && valor) {
        novo.codigo_grupo_empresa = null;
        novo.empreendimentos_selecionados = [];
      }
      if (campo === 'codigo_ge_vendedor') {
        novo.codigo_gerente = null;
        novo.codigo_supervisor = null;
        novo.codigo_assessor = null;
        novo.codigo_corretor = null;
      }
      if (campo === 'codigo_gerente') {
        novo.codigo_supervisor = null;
        novo.codigo_assessor = null;
        novo.codigo_corretor = null;
      }
      if (campo === 'codigo_supervisor') {
        novo.codigo_assessor = null;
        novo.codigo_corretor = null;
      }
      if (campo === 'codigo_assessor') {
        novo.codigo_corretor = null;
      }
      return novo;
    });
  };

  const handleLimpar = () => {
    setFiltros(filtrosIniciais);
    setEmpreendimentos([]);
    setHierarquia({
      gerentes: [],
      supervisores: [],
      assessores: [],
      corretores: [],
    });
    setDados([]);
    setFiltrosSubmetidos(null);
  };

  const montarFiltroRequest = f => {
    const tiposStr =
      f.tipos_empreendimento.length > 0
        ? f.tipos_empreendimento.map(t => `'${t}'`).join(',')
        : null;

    const listaEmpreendimentos =
      f.empreendimentos_selecionados.length > 0
        ? f.empreendimentos_selecionados.join(',')
        : null;

    let codigoComissao = null;
    let tipoComissao = null;
    if (f.codigo_corretor) {
      codigoComissao = f.codigo_corretor;
      tipoComissao = 'COR';
    } else if (f.codigo_assessor) {
      codigoComissao = f.codigo_assessor;
      tipoComissao = 'EQP';
    } else if (f.codigo_supervisor) {
      codigoComissao = f.codigo_supervisor;
      tipoComissao = 'SUP';
    } else if (f.codigo_gerente) {
      codigoComissao = f.codigo_gerente;
      tipoComissao = 'GER';
    }

    const incluirPropostas = f.periodo_referente === 'P' ? 'S' : 'N';

    // Backend desserializa DateTime; enviamos objeto Date (ISO) como no Ranking de Vendas.
    // O backend força 00:00:00 / 23:59:59 (URelVendasValorAvista.pas:449-450).
    return {
      data_inicial: moment(f.data_inicial, 'DD/MM/YYYY').toDate(),
      data_final: moment(f.data_final, 'DD/MM/YYYY').toDate(),
      codigo_grupo_empresa: f.codigo_grupo_empresa,
      codigo_grupo_ge: f.codigo_grupo_ge,
      lista_empreendimentos: listaEmpreendimentos,
      tipo_empreendimento: tiposStr,
      codigo_comissao: codigoComissao,
      codigo_ge_vendedor: f.codigo_ge_vendedor,
      tipo_comissao: tipoComissao,
      incluir_propostas: incluirPropostas,
      excluir_transferencias: f.excluir_transferencias,
      filtro_gerencial: f.filtro_gerencial,
      ocultar_sinal_parceiro: f.ocultar_sinal_parceiro,
      tipo_vv: f.tipo_vv,
      somente_int_vendas: f.somente_int_vendas,
      considerar_responsavel: f.considerar_responsavel,
      filtrar_rescisao_data: f.filtrar_rescisao_data,
    };
  };

  const handleSubmit = async () => {
    if (!filtros.data_inicial || !filtros.data_final) {
      toast.error('Período inválido!');
      return;
    }
    if (
      !filtros.tipos_empreendimento ||
      filtros.tipos_empreendimento.length === 0
    ) {
      toast.error('Nenhum tipo de Empreendimento selecionado');
      return;
    }

    setIsLoading(true);
    setDados([]);
    try {
      const filtroReq = montarFiltroRequest(filtros);
      setFiltrosSubmetidos(filtroReq);
      const resultado = await api.consultarVendasValorVista(filtroReq);
      if (!resultado || resultado.length === 0) {
        toast.info('Não existem dados para o período');
        setDados([]);
      } else {
        setDados(resultado);
      }
    } catch (err) {
      const msg =
        err && err.message ? err.message : 'Erro ao consultar relatório';
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleExportarExcel = async () => {
    if (!filtrosSubmetidos) return;
    setIsExporting(true);
    try {
      const { salvarEmExcel } = await import('~/Utils/Helpers');
      await salvarEmExcel(
        filtrosSubmetidos,
        '/relatorios/vendas-valor-vista/exportar-excel'
      );
    } catch (err) {
      const msg =
        err && err.message ? err.message : 'Erro ao exportar para Excel';
      toast.error(msg);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Container fluid className="px-3 py-3">
      <HeaderAzul title="Vendas por Valor à Vista" />

      <FiltrosRelatorio
        filtros={filtros}
        grupoEmpresas={grupoEmpresas}
        gruposGe={gruposGe}
        empreendimentos={empreendimentos}
        hierarquia={hierarquia}
        isLoading={isLoading}
        isExporting={isExporting}
        temDados={dados.length > 0}
        onFiltroChange={handleFiltroChange}
        onSubmit={handleSubmit}
        onLimpar={handleLimpar}
        onExportarExcel={handleExportarExcel}
      />

      {isLoading && <Loading label="Carregando relatório..." />}

      {!isLoading && dados.length > 0 && (
        <Row>
          <Col xs={12}>
            <TabelaResultado
              dados={dados}
              ocultarSinalParceiro={filtros.ocultar_sinal_parceiro === 'S'}
            />
          </Col>
        </Row>
      )}
    </Container>
  );
};

export default RelatorioVendasValorVista;
